import React, { useEffect, useState } from "react";
import Dashboard from "../components/Dashboard";
import config from "../config";
import { handleViewDetails } from "../components/ViewDetailsHandler";
import {
  ObjectPageHeader,
  PageContainer,
  FilterBar,
  Field,
  Select,
  SearchInput,
  FioriButton,
  FioriSwitch,
  StatusPill,
  ResponsiveTable,
  Spinner,
  EmptyState,
  Dialog,
  IconButton,
  ActionsCell,
} from "../components/FioriUI";

const url = config.api.orders;

function CreditMemo() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    exceptionType: "",
    search: "",
    customer: "",
    processedTime: "",
  });
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage] = useState("");
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsHtml, setDetailsHtml] = useState("");
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [detailsModalExceptionId, setDetailsModalExceptionId] = useState(null);
  const [rowToRefresh, setRowToRefresh] = useState(null);
  const [rowLoading, setRowLoading] = useState(null);
  const [customerList, setCustomerList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [returnModal, setReturnModal] = useState({
    show: false,
    salesOrder: null,
    rowData: null,
  });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminEmailErrors, setAdminEmailErrors] = useState({});
  const [expandedActions, setExpandedActions] = useState({});
  const [view, setView] = useState("");
  const isDashboard = view === "dashboard";

  useEffect(() => {
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        let apiData = json.total_orders || [];
        if (!Array.isArray(apiData)) apiData = [];
        setData(apiData);
        setFilteredData(apiData);
        setLoading(false);
        setCustomerList(
          Array.from(new Set(apiData.map((i) => i.vendor).filter(Boolean))),
        );
        setStatusList(
          Array.from(new Set(apiData.map((i) => i.status).filter(Boolean))),
        );
      })
      .catch(() => {
        setData([]);
        setFilteredData([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let filtered = [...data];
    if (filters.status) {
      filtered = filtered.filter(
        (i) =>
          i.status && i.status.toUpperCase() === filters.status.toUpperCase(),
      );
    }
    if (filters.exceptionType) {
      filtered = filtered.filter(
        (i) => i.exception_category === filters.exceptionType,
      );
    }
    if (filters.customer) {
      filtered = filtered.filter((i) => i.vendor === filters.customer);
    }
    if (filters.processedTime) {
      filtered = filtered.filter(
        (i) => i.processed_time === filters.processedTime,
      );
    }
    if (filters.search) {
      const s = filters.search.toLowerCase();
      filtered = filtered.filter(
        (i) =>
          i.sales_order?.toLowerCase().includes(s) ||
          i.vendor?.toLowerCase().includes(s) ||
          i.exception_category?.toLowerCase().includes(s) ||
          i.invoice_number?.toLowerCase().includes(s),
      );
    }
    setFilteredData(filtered);
  }, [data, filters]);

  const getValue = (row, key) => row[key] || "-";
  const handleFilterChange = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const handleClearFilters = () =>
    setFilters({
      status: "",
      exceptionType: "",
      search: "",
      customer: "",
      processedTime: "",
    });

  const handleRefreshData = () => {
    setLoading(true);
    fetch(url)
      .then((res) => res.json())
      .then((json) => {
        let apiData = json.total_orders || [];
        if (!Array.isArray(apiData)) apiData = [];
        setData(apiData);
        setFilteredData(apiData);
        setLoading(false);
      })
      .catch(() => {
        setData([]);
        setFilteredData([]);
        setLoading(false);
      });
  };

  const validateAdminEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSettingsSave = () => {
    const errors = {};
    if (!adminEmail) errors.adminEmail = "Admin email is required";
    else if (!validateAdminEmail(adminEmail))
      errors.adminEmail = "Please enter a valid email address";
    setAdminEmailErrors(errors);
    if (Object.keys(errors).length === 0) setShowSettingsModal(false);
  };

  const handleWarningClose = async () => {
    setShowWarning(false);
    if (rowToRefresh) {
      setRowLoading(rowToRefresh);
      const res = await fetch(url);
      const json = await res.json();
      const apiData = json.total_orders || [];
      const updatedRow = apiData.find(
        (item) => item.sales_order === rowToRefresh,
      );
      if (updatedRow) {
        setData((prev) =>
          prev.map((item) =>
            item.sales_order === rowToRefresh ? updatedRow : item,
          ),
        );
        setFilteredData((prev) =>
          prev.map((item) =>
            item.sales_order === rowToRefresh ? updatedRow : item,
          ),
        );
      }
      setRowToRefresh(null);
      setRowLoading(null);
    }
  };

  return (
    <>
      <ObjectPageHeader
        title="Credit Memo Management"
        subtitle="O2C exception handling: shipments awaiting credit memo"
        actions={
          <>
            <FioriSwitch
              checked={isDashboard}
              onChange={(v) => setView(v ? "dashboard" : "")}
              label="Dashboard"
            />
            <FioriButton
              variant="emphasized"
              onClick={handleRefreshData}
              disabled={loading}
              loading={loading}
            >
              {loading ? "Refreshing" : "Refresh"}
            </FioriButton>
          </>
        }
      />

      <PageContainer>
        <FilterBar
          title="Filter Credit Memos"
          onClear={handleClearFilters}
          footer={
            <SearchInput
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              placeholder="Search invoice, customer, sales order"
            />
          }
        >
          <Field label="Status" htmlFor="status">
            <Select
              id="status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">All</option>
              {statusList.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() +
                    status.slice(1).toLowerCase()}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Customer" htmlFor="customer">
            <Select
              id="customer"
              value={filters.customer}
              onChange={(e) => handleFilterChange("customer", e.target.value)}
            >
              <option value="">All</option>
              {customerList.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>
        </FilterBar>

        {isDashboard ? (
          <Dashboard filteredData={filteredData} source="credit-memo" />
        ) : (
        <ResponsiveTable>
          <thead>
            <tr>
              <th className="whitespace-nowrap">Invoice #</th>
              <th className="whitespace-nowrap">Customer</th>
              <th className="whitespace-nowrap">Sales Order</th>
              <th className="whitespace-nowrap">Shipment #</th>
              <th className="whitespace-nowrap">Amount</th>
              <th className="min-w-[280px]">Agent's Action</th>
              <th>Status</th>
              <th className="text-center whitespace-nowrap">Financial Posting</th>
              <th className="text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9}>
                  <div className="flex justify-center items-center gap-3 py-8 text-fiori-text-secondary text-sm">
                    <Spinner /> Loading credit memos
                  </div>
                </td>
              </tr>
            ) : !Array.isArray(filteredData) || filteredData.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <EmptyState
                    title="No credit memos"
                    description="No items match the current filters."
                  />
                </td>
              </tr>
            ) : (
              filteredData.map((row, idx) => (
                <tr key={idx}>
                  <td className="font-mono text-xs">
                    {getValue(row, "invoice_number")}
                  </td>
                  <td>{getValue(row, "vendor")}</td>
                  <td className="font-mono text-xs">
                    {getValue(row, "sales_order")}
                  </td>
                  <td className="font-mono text-xs">
                    {getValue(row, "shipment_number")}
                  </td>
                  <td className="whitespace-nowrap font-mono">
                    USD {getValue(row, "amount")}
                  </td>
                  <td>
                    <ActionsCell
                      items={row.action_performed?.action_details}
                      expanded={!!expandedActions[idx]}
                      onToggle={() =>
                        setExpandedActions((p) => ({ ...p, [idx]: !p[idx] }))
                      }
                    />
                  </td>
                  <td>
                    <StatusPill status={row.status} />
                  </td>
                  <td className="text-center">
                    {(row.status || "").toUpperCase() === "POSTED" &&
                      detailsModalExceptionId !== row.sales_order && (
                        <IconButton
                          title="View Financial Posting"
                          onClick={() =>
                            handleViewDetails(
                              row.sales_order,
                              setDetailsLoading,
                              setDetailsError,
                              setShowDetailsModal,
                              setDetailsModalExceptionId,
                              setDetailsHtml,
                            )
                          }
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                            stroke="currentColor"
                            className="w-4 h-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.25 12C2.25 12 5.25 6.75 12 6.75C18.75 6.75 21.75 12 21.75 12C21.75 12 18.75 17.25 12 17.25C5.25 17.25 2.25 12 2.25 12Z"
                            />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </IconButton>
                      )}
                  </td>
                  <td className="text-right">
                    {rowLoading === row.sales_order ? (
                      <Spinner size="sm" />
                    ) : (
                      <FioriButton
                        variant="emphasized"
                        disabled={
                          !["OPEN", "CANCELLED"].includes(
                            (row.status || "").toUpperCase(),
                          )
                        }
                        onClick={
                          ["OPEN", "CANCELLED"].includes(
                            (row.status || "").toUpperCase(),
                          )
                            ? () =>
                                setReturnModal({
                                  show: true,
                                  salesOrder: row.sales_order,
                                  rowData: row,
                                })
                            : undefined
                        }
                      >
                        Return
                      </FioriButton>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </ResponsiveTable>
        )}
      </PageContainer>

      {/* Warning dialog */}
      <Dialog
        show={showWarning}
        title="Successfully Initiated"
        size="sm"
        onClose={handleWarningClose}
        footer={
          <FioriButton variant="emphasized" onClick={handleWarningClose}>
            OK
          </FioriButton>
        }
      >
        <div className="px-4 py-5 text-sm text-fiori-text">
          {warningMessage}
        </div>
      </Dialog>

      {/* Success popup */}
      <Dialog
        show={showSuccessPopup}
        title="Email Sent"
        size="sm"
        onClose={() => {
          setShowSuccessPopup(false);
          handleRefreshData();
        }}
        footer={
          <FioriButton
            variant="emphasized"
            onClick={() => {
              setShowSuccessPopup(false);
              handleRefreshData();
            }}
          >
            OK
          </FioriButton>
        }
      >
        <div className="px-4 py-6 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-fiori-success-bg flex items-center justify-center mb-3">
            <svg
              className="w-6 h-6 text-fiori-success"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <p className="text-sm text-fiori-text">Email sent successfully.</p>
        </div>
      </Dialog>

      {/* Details (financial posting) dialog */}
      <Dialog
        show={showDetailsModal}
        title="Financial Posting"
        size="lg"
        onClose={() => {
          setShowDetailsModal(false);
          setDetailsModalExceptionId(null);
        }}
        footer={
          <FioriButton
            onClick={() => {
              setShowDetailsModal(false);
              setDetailsModalExceptionId(null);
            }}
          >
            Close
          </FioriButton>
        }
      >
        <div className="p-4">
          {detailsLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[150px] gap-3">
              <Spinner size="lg" />
              <p className="text-sm text-fiori-text-secondary">Loading...</p>
            </div>
          ) : detailsError ? (
            <p className="text-center text-fiori-error py-6">{detailsError}</p>
          ) : (
            <div
              className="text-sm text-fiori-text"
              dangerouslySetInnerHTML={{ __html: detailsHtml }}
            />
          )}
        </div>
      </Dialog>

      {/* Return (compose email) dialog */}
      <ReturnDialog
        show={returnModal.show}
        rowData={returnModal.rowData}
        onClose={() =>
          setReturnModal({ show: false, salesOrder: null, rowData: null })
        }
        onSuccess={() => {
          setReturnModal({ show: false, salesOrder: null, rowData: null });
          setShowSuccessPopup(true);
        }}
      />

      {/* Settings dialog */}
      <Dialog
        show={showSettingsModal}
        title="Settings"
        size="md"
        onClose={() => setShowSettingsModal(false)}
        footer={
          <>
            <FioriButton onClick={() => setShowSettingsModal(false)}>
              Cancel
            </FioriButton>
            <FioriButton variant="emphasized" onClick={handleSettingsSave}>
              Save
            </FioriButton>
          </>
        }
      >
        <div className="p-4 space-y-3">
          <Field label="Admin Email" htmlFor="admin-email">
            <input
              id="admin-email"
              type="email"
              value={adminEmail}
              onChange={(e) => {
                setAdminEmail(e.target.value);
                if (adminEmailErrors.adminEmail)
                  setAdminEmailErrors((p) => ({ ...p, adminEmail: null }));
              }}
              className={`fiori-input ${
                adminEmailErrors.adminEmail ? "border-fiori-error" : ""
              }`}
              placeholder="Enter admin email"
              autoFocus
            />
            {adminEmailErrors.adminEmail && (
              <p className="text-xs text-fiori-error mt-1">
                {adminEmailErrors.adminEmail}
              </p>
            )}
          </Field>
        </div>
      </Dialog>
    </>
  );
}

/* ─────────────────────────── ReturnDialog ─────────────────────────── */
function ReturnDialog({ show, rowData, onClose, onSuccess }) {
  const [emailForm, setEmailForm] = useState({
    sender: "",
    subject: "",
    body: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const getValue = (row, key) => (row && row[key]) || "-";

  useEffect(() => {
    if (show && rowData) {
      const currentDate = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      setEmailForm({
        sender: "nitin.wagh@karini.ai",
        subject: `URGENT: Shipment Refusal - Invoice #${getValue(
          rowData,
          "invoice_number",
        )}`,
        body: `Dear Accounts Receivable Team,
We are writing to formally refuse the shipment received on ${currentDate}, and request immediate processing of a credit memo for the following:

Shipment Details:
Billing Invoice Number: ${getValue(rowData, "invoice_number")}
Sales Order: ${getValue(rowData, "sales_order")}
Shipment number: ${getValue(rowData, "shipment_number") || "N/A"}
Customer Number: ${getValue(rowData, "customer_number") || "N/A"}
Customer Name: ${getValue(rowData, "vendor")}`,
      });
    }
  }, [show, rowData]);

  const handleSend = async () => {
    const e = {};
    if (!emailForm.sender) e.sender = "Email is required";
    else if (!validateEmail(emailForm.sender))
      e.sender = "Please enter a valid email address";
    if (!emailForm.subject) e.subject = "Subject is required";
    if (!emailForm.body) e.body = "Message body is required";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setIsLoading(true);
    try {
      await fetch(
        config.api.return,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sender: "customer@karini.ai",
            subject: emailForm.subject,
            body: emailForm.body,
            adminEmail: "nitin.wagh@karini.ai",
            invoiceNumber: getValue(rowData, "invoice_number"),
          }),
        },
      );
      setEmailForm({ sender: "", subject: "", body: "" });
      onSuccess?.();
    } catch {
      alert("Error sending email");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      show={show}
      title="Return Shipment"
      size="lg"
      onClose={onClose}
      footer={
        <>
          <FioriButton onClick={onClose}>Cancel</FioriButton>
          <FioriButton
            variant="emphasized"
            onClick={handleSend}
            disabled={isLoading}
            loading={isLoading}
          >
            {isLoading ? "Sending..." : "Send"}
          </FioriButton>
        </>
      }
    >
      <div className="p-4 space-y-3">
        <Field label="From">
          <input
            type="text"
            value="Customer"
            readOnly
            disabled
            className="fiori-input bg-fiori-page-2/50 cursor-not-allowed"
          />
        </Field>

        <Field label="To">
          <input
            type="email"
            placeholder="Enter recipient email address"
            value={emailForm.sender}
            disabled
            onChange={(e) => {
              setEmailForm((p) => ({ ...p, sender: e.target.value }));
              if (errors.sender) setErrors((p) => ({ ...p, sender: null }));
            }}
            className={`fiori-input ${
              errors.sender ? "border-fiori-error" : ""
            }`}
            autoFocus
          />
          {errors.sender && (
            <p className="text-xs text-fiori-error mt-1">{errors.sender}</p>
          )}
        </Field>

        <Field label="Subject">
          <input
            type="text"
            placeholder="Enter subject"
            value={emailForm.subject}
            onChange={(e) => {
              setEmailForm((p) => ({ ...p, subject: e.target.value }));
              if (errors.subject) setErrors((p) => ({ ...p, subject: null }));
            }}
            className={`fiori-input ${
              errors.subject ? "border-fiori-error" : ""
            }`}
          />
          {errors.subject && (
            <p className="text-xs text-fiori-error mt-1">{errors.subject}</p>
          )}
        </Field>

        <Field label="Message">
          <textarea
            value={emailForm.body}
            onChange={(e) => {
              setEmailForm((p) => ({ ...p, body: e.target.value }));
              if (errors.body) setErrors((p) => ({ ...p, body: null }));
            }}
            placeholder="Compose email"
            rows={10}
            className={`fiori-input resize-none ${
              errors.body ? "border-fiori-error" : ""
            }`}
          />
          {errors.body && (
            <p className="text-xs text-fiori-error mt-1">{errors.body}</p>
          )}
        </Field>
      </div>
    </Dialog>
  );
}

export default CreditMemo;
